import {
  IpGeoInfo,
  KycLevels,
  KycLevelsRaw,
  KycLinkResponse,
  KycResponse,
  LoginPayload,
  LoginResponse,
  PaybisPaymentMethod,
  PaybisPaymentStatus,
  PaybisQuote,
  PaybisRequest,
  SessionResponse,
  SessionsTypes,
  SignupPayload,
  SignupResponse,
  UserData,
} from './types';
import baseApiPs2 from '../baseApiPs2';
import { injectEndpoints } from 'apis/util';
import { currencyTo } from '../../views/Dashboard/components/ManageInvestmentModals/forms/Paybis/constants';

export const api = injectEndpoints(baseApiPs2, (builder) => ({
  signup: builder.mutation<SignupResponse, SignupPayload>({
    query: (credentials) => ({
      url: 'signup',
      method: 'POST',
      body: credentials,
    }),
  }),
  logout: builder.mutation<void, void>({
    query: () => ({
      url: 'logout',
      method: 'POST',
    }),
  }),
  googleOauth: builder.mutation<
    LoginResponse & { action: SessionsTypes; justLinked: boolean },
    { code: string; ref: string; subtrack: string }
  >({
    query: ({ code, ref, subtrack }) => ({
      url: `connect/google`,
      method: 'POST',
      params: { ref, subtrack, code },
    }),
  }),
  login: builder.mutation<LoginResponse, LoginPayload>({
    query: (credentials) => ({
      url: 'login',
      method: 'POST',
      body: credentials,
    }),
  }),

  verifyCodeNewUser: builder.mutation<void, { code: string }>({
    query: ({ code }) => ({
      url: `user/verify_code/verify_email`,
      method: 'POST',
      body: { code },
    }),
  }),

  resendCodeNewUser: builder.mutation<void, void>({
    query: () => ({
      url: `user/resend_code/verify_email`,
      method: 'POST',
    }),
  }),
  sendCodeWithdraw: builder.mutation<
    void,
    {
      network: string;
      asset: string;
      tag: string;
      address: string;
      amount: string;
    }
  >({
    query: (params) => ({
      url: `user/resend_code/withdraw`,
      method: 'POST',
      body: params,
    }),
  }),
  resendCode: builder.mutation<void, string>({
    query: (reason) => ({
      url: `user/resend_code/${reason}`,
      method: 'POST',
    }),
  }),

  verifyCode: builder.mutation<void, { code: string }>({
    query: ({ code }) => ({
      url: `user/verify_code/enable_user`,
      method: 'POST',
      body: { code },
    }),
  }),

  resendCodeEnableUser: builder.mutation<void, void>({
    query: () => ({
      url: `user/resend_code/enable_user`,
      method: 'POST',
    }),
  }),

  verify2FA: builder.mutation<void, { code: string }>({
    query: ({ code }) => ({
      url: `user/verify_2fa`,
      method: 'POST',
      body: { code2FA: code },
    }),
  }),

  verifyKnownDevice: builder.mutation<void, { code: string }>({
    query: ({ code }) => ({
      url: `known_device/verify`,
      method: 'POST',
      body: { code },
    }),
  }),

  resendKnownDeviceCode: builder.mutation<void, void>({
    query: () => ({
      url: `/known_device/resend`,
      method: 'POST',
    }),
  }),

  setLocale: builder.mutation<void, { locale: string }>({
    query: ({ locale }) => ({
      url: `/user/save_locale`,
      method: 'POST',
      body: {
        // this should be redundant because we only have 2-letter lcoales
        locale: locale?.slice(0, 2),
      },
    }),
  }),

  user: builder.query<UserData, void>({
    query: () => ({
      url: 'user',
      headers: { 'Accept-version': '2' },
    }),
  }),

  userAcceptTos: builder.mutation({
    query: () => ({
      url: `user/accept_tos`,
      method: 'POST',
    }),
  }),

  session: builder.query<SessionResponse, void>({
    query: () => ({
      url: 'user/session',
    }),
  }),

  kycLink: builder.query<
    KycLinkResponse,
    { category: string; level: number; isTrader?: boolean }
  >({
    query: ({ category, level, isTrader }) => ({
      url: `user/kyc/${category}/${level}/link${isTrader ? '/binance' : ''}`,
    }),
  }),

  kycStatuses: builder.query<
    KycResponse,
    'shufti' | 'binance' | 'sumsub' | undefined
  >({
    query: (provider) => ({
      url: `user/kyc/status${provider ? `/${provider}` : ''}`,
    }),
  }),

  kycLevels: builder.query<KycLevels, void>({
    query: () => ({
      url: `user/kyc/levels`,
    }),
    transformResponse: (response: KycLevelsRaw) =>
      response.levels.reduce((acc, curr) => {
        const categoryObj = acc.find((c) => c.category === curr.category);
        if (categoryObj) {
          categoryObj.levels.push(curr);
        } else {
          acc.push({ category: curr.category, levels: [curr] });
        }
        return acc;
      }, []),
  }),

  activateExchange: builder.mutation<void, { exchangeInternalId: string }>({
    query: ({ exchangeInternalId }) => ({
      url: `/user/exchanges/${exchangeInternalId}/activate`,
      method: 'POST',
    }),
  }),

  updateUser: builder.mutation<
    void,
    {
      userName?: string;
      imageUrl?: string;
      payFeeWithZig?: boolean;
      tradingFeeDiscount?: boolean;
      refRewardType?: string;
      email?: string;
      about?: string;
      countryCode: string;
    }
  >({
    query: (params) => ({
      url: `/user`,
      method: 'POST',
      body: params,
    }),
  }),

  resetPasswordRequest: builder.mutation<
    void,
    {
      email: string;
    }
  >({
    query: (params) => ({
      url: `/user/request_action/forgotten_password`,
      method: 'POST',
      body: params,
    }),
  }),

  resetPassword: builder.mutation<
    void,
    {
      token: string;
      password: string;
    }
  >({
    query: (params) => ({
      url: `/user/confirm_action/forgotten_password`,
      method: 'POST',
      body: params,
    }),
  }),

  deleteAccount: builder.mutation<
    void,
    {
      code2FA: string;
      code: string;
    }
  >({
    query: (params) => ({
      url: `/user/delete_account`,
      method: 'POST',
      body: params,
    }),
  }),

  updatePassword: builder.mutation<
    void,
    {
      password: string;
      newPassword: string;
      code: string;
      code2FA?: string;
    }
  >({
    query: (params) => ({
      url: `/change_password`,
      method: 'POST',
      body: params,
    }),
  }),

  disable2FA: builder.mutation<
    void,
    {
      code: string;
      code2FA?: string;
    }
  >({
    query: (params) => ({
      url: `/user/disable_2fa`,
      method: 'POST',
      body: params,
    }),
  }),

  storeGtagClientIdSessionId: builder.mutation<
    void,
    {
      clientId: string;
      sessionId: string;
    }
  >({
    query: ({ clientId, sessionId }) => ({
      url: `/user/ga_ids`,
      method: 'POST',
      body: {
        ga_client_id: clientId,
        ga_session_id: sessionId,
      },
    }),
    extraOptions: {
      // do not report errors
      // we use the text handler so it's not parsed properly
      silent: true,
    },
  }),

  enable2FAInfo: builder.query<
    { secret: string; image: string },
    { code: string }
  >({
    query: ({ code }) => ({
      url: `/user/enable_2fa/step1`,
      method: 'POST',
      body: {
        code,
      },
    }),
  }),

  paybisRequestStatus: builder.query<PaybisPaymentStatus, string>({
    query: (requestId) => ({
      url: `/gateway/on_offramp/status/${requestId}`,
    }),
    extraOptions: {
      silent: true,
    },
  }),

  enable2FA: builder.mutation<
    void,
    {
      code: string;
      code2FA?: string;
    }
  >({
    query: (params) => ({
      url: `/user/enable_2fa/step2`,
      method: 'POST',
      body: params,
    }),
  }),

  paybisQuote: builder.mutation<
    PaybisQuote,
    {
      amount: string | number;
      currency: string;
      method: string;
    }
  >({
    query: ({ amount, currency, method }) => ({
      url: `/gateway/onramp/quote`,
      method: 'POST',
      body: {
        currencyCodeFrom: currency,
        currencyCodeTo: currencyTo,
        amount: amount.toString(),
        type: 'deposit',
        paymentMethod: method,
        isReceivedAmount: true,
      },
    }),
  }),

  paybisPaymentMethods: builder.query<
    { paymentMethods: PaybisPaymentMethod[] },
    { currencyFrom: string; currencyTo: string }
  >({
    query: ({ currencyFrom, currencyTo }) => ({
      url: `/gateway/on_offramp/payment-methods/${currencyFrom}/${currencyTo}`,
    }),
  }),

  paybisDeposit: builder.mutation<
    PaybisRequest,
    {
      userId: string;
      quoteId: string;
      currencyCodeTo: string;
      userIp: string;
    }
  >({
    query: (body) => ({
      url: `/gateway/onramp/deposit`,
      method: 'POST',
      body,
    }),
  }),

  userIpInfo: builder.query<IpGeoInfo, void>({
    queryFn: async () => {
      try {
        const response = await fetch(
          `https://api.ipgeolocation.io/ipgeo?apiKey=${import.meta.env.VITE_IPGEOLOCATION_API_KEY}`,
        );
        return { data: await response.json() };
      } catch {
        return { data: { ip: '127.0.0.1', currency: { code: 'USD' } } };
      }
    },
  }),
}));

export const {
  useSignupMutation,
  useLogoutMutation,
  useLoginMutation,
  useLazyUserQuery,
  useLazySessionQuery,
  useActivateExchangeMutation,
  useVerify2FAMutation,
  useVerifyCodeMutation,
  useSessionQuery,
  useVerifyCodeNewUserMutation,
  useVerifyKnownDeviceMutation,
  useSetLocaleMutation,
  useResendCodeMutation,
  useResendCodeEnableUserMutation,
  useStoreGtagClientIdSessionIdMutation,
  useResendCodeNewUserMutation,
  useResendKnownDeviceCodeMutation,
  useUpdateUserMutation,
  useResetPasswordRequestMutation,
  useResetPasswordMutation,
  useUpdatePasswordMutation,
  useDisable2FAMutation,
  useLazyEnable2FAInfoQuery,
  useLazyKycLinkQuery,
  useKycStatusesQuery,
  useKycLevelsQuery,
  usePaybisQuoteMutation,
  usePaybisDepositMutation,
  usePaybisRequestStatusQuery,
  usePaybisPaymentMethodsQuery,
  useDeleteAccountMutation,
  useUserAcceptTosMutation,
  useEnable2FAMutation,
  useSendCodeWithdrawMutation,
  useGoogleOauthMutation,
  useUserIpInfoQuery,
} = api;
